<template>
  <div>
    <v-container id="global-filters-list" fluid class="mb-4 pt-0 d-flex pl-2">
      <v-row no-gutters class="d-flex align-center" justify="space-between">
        <v-col cols="3" sm="6" md="4">
          <analyze-search v-model="search" :hide-details="true" />
        </v-col>
        <v-col>
          <v-btn
            id="create-new-filter-btn"
            color="cyan-dark"
            variant="flat"
            class="ml-5 create-filter-btn"
            :prepend-icon="CONSTANTS.ICONS_MDI.ICON_EDIT_OUTLINE"
            @click="createNewGlobalFilter"
          >
            {{ labels.GLOBAL_FILTER.CREATE_NEW_GLOBAL_FILTER }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <analyze-data-table
      table-id="global-filters-table-id"
      :headers="filterHeaders"
      :fixed-header="true"
      :fixed-footer="true"
      :max-height="40"
      :items="filterItems"
      :has-apply-action="true"
      :has-edit-action="true"
      :has-delete-action="true"
      :search="search"
      :total-items="filterItems.length"
      data-type="filters"
      @apply="applyGlobalFilter"
      @edit="openGlobalFilterDialogInEditMode"
      @delete="invokeDeletionDialog"
    />
  </div>

  <global-filter-creation-dialog
    :is-opened="openDialog"
    :filter="selectedFilter"
    :is-view-mode="isViewMode"
    @close-dialog="openDialog = false"
    @disable-view-mode="isViewMode = false"
  />

  <ConfirmDeletionDialog
    v-if="selectedGlobalFilterToDelete"
    :key="`dialog-delete-global-filter${selectedGlobalFilterToDelete?.id}`"
    :element-to-delete="selectedGlobalFilterToDelete"
    :get-element-name="(p?: GlobalFilter) => p?.name ?? 'My Global Filter'"
    :element-type-name="labels.DELETE_ELEMENT.GLOBAL_FILTTER"
    :is-deleting-element="isDeletingGlobalFilter"
    :extra-confirmation-required="true"
    @confirm-deletion="deleteGlobalFilter"
    @cancel-deletion="selectedGlobalFilterToDelete = undefined"
  />
</template>

<script lang="ts" setup>
  import GlobalFilterCreationDialog from './GlobalFilterCreationDialog.vue'
  import CONSTANTS from '@/constants/Config.constants'
  import { useGlobalFilterStore } from '@/stores/globalFilterStore'
  import labels from '@/translations/labels.json'
  import { computed, ref, watch } from 'vue'
  import ConfirmDeletionDialog from '@/components/ConfirmDeletionDialog/ConfirmDeletionDialog.vue'
  import { GlobalFilter } from '@/models'
  import { useProjectStore } from '@/stores/projectStore'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import { parseIntUndefined } from '@/utils/helpers'
  import { storeToRefs } from 'pinia'
  import { useRoute } from 'vue-router'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'

  const globalFilterStore = useGlobalFilterStore()
  const { currentProject } = storeToRefs(useProjectStore())

  const route = useRoute()

  const selectedFilter = ref<GlobalFilter | undefined>(undefined)

  const search = ref<string>('')

  const openDialog = ref(false)
  const isViewMode = ref(false)
  const selectedGlobalFilterToDelete = ref<GlobalFilter | undefined>()
  const isDeletingGlobalFilter = ref(false)

  const filterHeaders = ref<AnalyzeTableHeader[]>([
    { title: '', key: 'selected', width: '58' },
    { title: 'Name', key: 'name' },
    { title: '', key: 'actions', align: 'end', sortable: false },
  ])

  const filterItems = computed((): AnalyzeTableItem<GlobalFilter>[] =>
    globalFilterStore.globalFilters.map((globalFilter) => ({
      name: globalFilter.name,
      value: globalFilter,
      selected: globalFilter.id === globalFilterStore.selectedGlobalFilterId,
    })),
  )

  watch(
    currentProject,
    (project) => {
      if (project) {
        globalFilterStore.loadGlobalFiltersFromApi(
          project.id,
          parseIntUndefined(route.query.filter as string),
        )
      }
    },
    { immediate: true },
  )

  function createNewGlobalFilter(): void {
    openGlobalFilterDialog(false, undefined)
  }

  function openGlobalFilterDialogInEditMode(filter?: GlobalFilter): void {
    if (filter) {
      openGlobalFilterDialog(true, filter as GlobalFilter)
    }
  }

  function openGlobalFilterDialog(isInViewMode: boolean, filter?: GlobalFilter): void {
    selectedFilter.value = filter
    openDialog.value = true
    isViewMode.value = isInViewMode
  }

  function applyGlobalFilter(filter?: GlobalFilter): void {
    if (filter) {
      globalFilterStore.applyGlobalFilter(filter as GlobalFilter)
    }
  }

  function invokeDeletionDialog(item?: GlobalFilter): void {
    if (item) {
      selectedGlobalFilterToDelete.value = item as GlobalFilter
    }
  }

  async function deleteGlobalFilter(): Promise<void> {
    if (!currentProject.value || !selectedGlobalFilterToDelete.value) {
      return Promise.resolve()
    }

    isDeletingGlobalFilter.value = true

    if (
      selectedGlobalFilterToDelete.value &&
      selectedGlobalFilterToDelete.value.id === globalFilterStore.selectedGlobalFilterId
    ) {
      globalFilterStore.applyGlobalFilter(undefined)
    }

    await globalFilterStore.deleteGlobalFilter(
      currentProject.value.id,
      selectedGlobalFilterToDelete.value,
    )

    isDeletingGlobalFilter.value = false
    selectedGlobalFilterToDelete.value = undefined
  }
</script>

<style scoped>
  .create-filter-btn {
    text-transform: none;
    padding-left: 19px;
  }

  .filter-title {
    &:hover {
      cursor: pointer;
    }
  }

  .filter-icon {
    &:hover {
      cursor: pointer;
    }
  }

  .border-style {
    border-radius: 5px;
    padding-right: 10px;
    padding-top: 2px;
    padding-bottom: 2px;

    &:hover {
      background-color: rgb(var(--v-theme-grey-light));
    }
  }

  .custom-tooltip :deep(.v-overlay__content) {
    background: rgb(var(--v-theme-grey-light));
    color: rgb(var(--v-theme-font-color));
  }
</style>
